import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'

const { logger } = Sentry;
import { useDarkMode } from '../../contexts/DarkModeContext'
import PurchaseCreditsModal from './PurchaseCreditsModal'
import './Header.scss'

function Header() {
  const { isAuthenticated, logout, user, loginWithPopup } = useAuth0()
  const { isDarkMode } = useDarkMode()
  const location = useLocation()
  const navigate = useNavigate()
  const isDashboard = location.pathname === '/dashboard'
  const isHome = location.pathname === '/'
  const [showPurchaseModal, setShowPurchaseModal] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  const handleLogout = () => {
    setShowUserMenu(false)
    logout({ logoutParams: { returnTo: window.location.origin } })
  }

  // Get avatar URL from user object
  const getAvatarUrl = (): string | null => {
    if (user?.picture) {
      return user.picture
    }
    if (user?.identities && Array.isArray(user.identities)) {
      for (const identity of user.identities) {
        if ((identity as any).avatarUrl) {
          return (identity as any).avatarUrl
        }
        if ((identity as any).picture) {
          return (identity as any).picture
        }
      }
    }
    return null
  }

  // Get user's initial for fallback
  const getUserInitial = (): string => {
    if (user?.name) {
      return user.name.charAt(0).toUpperCase()
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase()
    }
    return '?'
  }

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUserMenu])

  const handlePricingClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    if (isAuthenticated) {
      // If authenticated, show purchase credits modal
      setShowPurchaseModal(true)
    } else {
      // If not authenticated, scroll to pricing section
      if (isHome) {
        const pricingSection = document.getElementById('pricing')
        if (pricingSection) {
          pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      } else {
        navigate('/')
        setTimeout(() => {
          const pricingSection = document.getElementById('pricing')
          if (pricingSection) {
            pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        }, 100)
      }
    }
  }

  const handleGetStarted = async () => {
    setIsLoading(true)
    try {
      const width = 700
      const height = 900
      const left = window.screen.width / 2 - width / 2
      const top = window.screen.height / 2 - height / 2
      
      const popup = window.open(
        '',
        'auth0:authorize:popup',
        `width=${width},height=${height},left=${left},top=${top},resizable,scrollbars=yes,status=1`
      )
      
      await loginWithPopup({
        authorizationParams: {
          screen_hint: 'signup'
        }
      }, { popup } as any)
      // Navigation will happen via useEffect when isAuthenticated becomes true
    } catch (error) {
      logger.error('Login error', {
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsLoading(false)
    }
  }

  // Only redirect to dashboard if user is on home page and becomes authenticated
  // Don't redirect if user is already on a protected route (like /profile)
  useEffect(() => {
    if (isAuthenticated && location.pathname === '/') {
      navigate('/dashboard')
    }
  }, [isAuthenticated, navigate, location.pathname])

  return (
    <header className="app-header">
      <div className="header-container">
        <div className="header-content">
          <Link to={isAuthenticated ? "/dashboard" : "/"} className="header-logo">
            <img 
              src="/images/logo.svg" 
              alt="JobsReadyAI Logo" 
              className="logo-image"
            />
          </Link>
          <nav className="header-nav" aria-label="Main navigation">
            {isAuthenticated ? (
              <>
                {!isDashboard && (
                  <Link to="/dashboard" className="nav-link">
                    Dashboard
                  </Link>
                )}
                <button 
                  onClick={handlePricingClick}
                  className="btn-modern btn-primary btn-sm"
                  aria-label="Buy credits"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                    <line x1="1" y1="10" x2="23" y2="10"></line>
                  </svg>
                  Buy Credits
                </button>
                <div className="user-menu-container" ref={userMenuRef}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="user-menu-trigger"
                    aria-label="User menu"
                    aria-expanded={showUserMenu}
                  >
                    {getAvatarUrl() ? (
                      <img 
                        src={getAvatarUrl()!} 
                        alt={user?.name || user?.email || 'User'} 
                        className="user-avatar"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="user-initial-circle">
                        {getUserInitial()}
                      </div>
                    )}
                  </button>
                  {showUserMenu && (
                    <div className="user-menu-dropdown">
                      <Link 
                        to="/profile" 
                        className="user-menu-item"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                          <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                        Profile
                      </Link>
                      <Link 
                        to="/sharing" 
                        className="user-menu-item"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                        Invite and earn
                      </Link>
                      <Link 
                        to="/settings" 
                        className="user-menu-item"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="3"></circle>
                          <path d="M12 1v6m0 6v6M5.64 5.64l4.24 4.24m4.24 4.24l4.24 4.24M1 12h6m6 0h6M5.64 18.36l4.24-4.24m4.24-4.24l4.24-4.24"></path>
                        </svg>
                        Settings
                      </Link>
                      <div className="user-menu-divider"></div>
                      <button
                        onClick={handleLogout}
                        className="user-menu-item user-menu-item-button"
                        aria-label="Log out"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                          <polyline points="16 17 21 12 16 7"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12"></line>
                        </svg>
                        Log Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <a 
                  href="#pricing" 
                  onClick={handlePricingClick}
                  className="nav-link"
                  aria-label="View pricing"
                >
                  Pricing
                </a>
                <a 
                  href="http://jobsreadyai.medium.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="nav-link" 
                  aria-label="Read our blog"
                >
                  Blog
                </a>
                <button 
                  onClick={handleGetStarted}
                  className="btn-modern btn-primary btn-sm"
                  aria-label="Get started with JobsReadyAI"
                  disabled={isLoading}
                >
                  {isLoading ? 'Loading...' : 'Get Started'}
                </button>
              </>
            )}
          </nav>
        </div>
      </div>
      <PurchaseCreditsModal show={showPurchaseModal} onHide={() => setShowPurchaseModal(false)} />
    </header>
  )
}

export default Header
